import moment from 'moment-timezone';

import Lead from 'classes/Lead.js';
import Request from 'files/Request.js';
import User from 'classes/User.js';

class AppointmentClass {

    booked_by = null;
    booked_by_user_id = null;
    created = null;
    duration = null;
    end_date = null;
    feedback_template = null; // internal use only
    feedback_template_id = null;
    id = null;
    lead = null;
    meeting_id = null;
    notes = null;
    primary_user = null;
    primary_user_id = null;
    qualified = null;
    start_date = null;
    status = null;
    timezone = null;

    constructor() {
        return this;
    }

    create = (props = {}) => {
        this.booked_by = props.booked_by && User.create(props.booked_by);
        this.booked_by_user_id = props.booked_by_user_id;
        this.created = props.created && moment.utc(props.created).local();
        this.duration = props.duration;
        this.end_date = props.end_date && moment.utc(props.end_date);
        this.feedback_template_id = props.feedback_template_id;
        this.id = props.id;
        this.lead = props.lead && Lead.create(props.lead);
        this.meeting_id = props.meeting_id;
        this.notes = props.notes;
        this.primary_user = props.primary_user && User.create(props.primary_user);
        this.primary_user_id = props.primary_user_id;
        this.qualified = props.qualified;
        this.start_date = props.start_date && moment.utc(props.start_date);
        this.status = props.status;
        this.timezone = props.timezone;
        return this;
    }

    open = () => {
        this.edits = {
            end_date: this.end_date,
            feedback_template: this.feedback_template,
            primary_user: this.primary_user,
            qualified: this.qualified,
            start_date: this.start_date
        }
        return this.edits;
    }

    set = props => {
        this.edits = {
            ...this.edits,
            ...props
        }
        return this.edits;
    }

    close = () => {
        this.end_date = this.edits.end_date;
        this.feedback_template = this.edits.feedback_template;
        this.primary_user = this.edits.primary_user;
        this.qualified = this.edits.qualified;
        this.start_date = this.edits.start_date;
    }

    update = async (utils, props) => {
        return new Promise(async (resolve, reject) => {
            try {
                let { status } = await Request.post(utils, '/appointments/', {
                    type: 'update',
                    id: this.id,
                    ...this.toJSON(this.edits),
                    ...props
                })

                this.close();
                this.status = status;

                utils.content.update({
                    type: 'appointments',
                    object: this
                });
                resolve();

            } catch(e) {
                reject(e);
            }
        })
    }

    toJSON = props => {
        let target = props || this;
        return {
            end_date: target.end_date && moment(target.end_date).utc().format('YYYY-MM-DD HH:mm:ss'),
            feedback_template_id: target.feedback_template && target.feedback_template.id,
            id: this.id,
            primary_user_id: target.primary_user && target.primary_user.user_id,
            qualified: target.qualified,
            start_date: target.start_date && moment(target.start_date).utc().format('YYYY-MM-DD HH:mm:ss')
        }
    }
}

class AppointmentSlotClass {

    client_id = null;
    end_hour = null;
    id = null;
    name = null;
    start_hour = null;
    user_id = null;

    constructor() {
        return this;
    }

    apply = (utils, isNewTarget) => {
        return isNewTarget ? this.submit(utils) : this.update(utils);
    }

    close = () => {
        this.end_hour = this.edits.end_hour;
        this.name = this.edits.name;
        this.start_hour = this.edits.start_hour;
        return this;
    }

    create = (props = {}) => {
        this.client_id = props.client_id;
        this.end_hour = props.end_hour;
        this.id = props.id;
        this.name = props.name;
        this.start_hour = props.start_hour;
        this.user_id = props.user_id;
        return this;
    }

    open = () => {
        this.edits = {
            end_hour: this.end_hour,
            name: this.name,
            start_hour: this.start_hour
        }
        return this.edits;
    }

    set = props => {
        this.edits = {
            ...this.edits,
            ...props
        }
        return this.edits;
    }

    submit = utils => {
        return new Promise(async (resolve, reject) => {
            try {
                // submit request to server
                let { id } = await Request.post(utils, '/clients/', {
                    ...this.edits,
                    type: 'new_appointment_slot'
                });

                // set id and close edits
                this.id = id;
                this.close();

                // notify subscribers that slot data has changed
                utils.content.fetch('appointment_slots');

                resolve();

            } catch(e) {
                reject(e);
            }
        });
    }

    update = utils => {
        return new Promise(async (resolve, reject) => {
            try {
                // submit request to server
                await Request.post(utils, '/clients/', {
                    ...this.edits,
                    id: this.id,
                    type: 'update_appointment_slot'
                });

                // close edits
                this.close();

                // notify subscribers that slot data has changed
                utils.content.update({
                    object: this,
                    type: 'appointment_slots'
                });

                resolve();

            } catch(e) {
                reject(e);
            }
        });
    }
}

const fetchAppointment = async (utils, id) => {
    return new Promise(async (resolve, reject) => {
        try {
            let { appointment } = await Request.get(utils, '/appointments/', {
                type: 'details',
                id: id
            });
            let obj = new AppointmentClass().create(appointment);
            resolve(obj);

        } catch(e) {
            reject(e);
        }
    });
}

const statusCodes = {
    pending: -1,
    new: 1,
    set: 100,
    assigned: 125,
    confirmed: 150,
    sale: 200,
    recall: 250,
    do_not_call: 275,
    did_not_sell: 300,
    not_home: 400,
    not_interested: 450,
    incomplete: 500,
    wrong_number: 525,
    left_voicemail: 550,
    rescheduled: 700,
    cancelled: 800,
    turndown: 900,
    unqualified: 1000
};

export default {
    create: props => new AppointmentClass().create(props),
    get: (utils, id) => fetchAppointment(utils, id),
    status: {
        get: () => statusCodes
    },
    Slot: {
        create: props => new AppointmentSlotClass().create(props),
        new: () => new AppointmentSlotClass()
    }
}
